<script setup>
import { router } from '@inertiajs/vue3';
import DeleteAction from '@js/components/actions/DeleteAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import ColorField from '@js/components/fields/ColorField.vue';
import InputField from '@js/components/fields/InputField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormGrid from '@js/components/forms/FormGrid.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import { useRoute } from '@js/utils/route';
import { toastError, toastSaved } from '@js/utils/toast';
import { reactive, ref } from 'vue';
import { number, object, string } from 'yup';

const { newsTheme, nextPosition } = defineProps({
    newsTheme: {
        type: Object,
        required: false,
    },
    nextPosition: {
        type: Number,
        required: true,
    },
});

const { route } = useRoute();

const presetColors = [
    '#4F46E5',
    '#0EA5E9',
    '#10B981',
    '#F59E0B',
    '#EF4444',
    '#EC4899',
    '#6B7280',
];

const form = reactive({
    name: newsTheme ? newsTheme.name : '',
    color: newsTheme ? newsTheme.color : presetColors[0],
    position: newsTheme ? newsTheme.position : nextPosition,
});

const validationSchema = object({
    name: string().required('Ce champ est obligatoire.'),
    color: string()
        .matches(
            /^#[0-9A-Fa-f]{6}$/,
            'Veuillez saisir une couleur hexadécimale valide.',
        )
        .required('Ce champ est obligatoire.'),
    position: number()
        .typeError('Veuillez saisir un nombre entier.')
        .integer('Veuillez saisir un nombre entier.')
        .min(0, 'La position ne peut pas être négative.')
        .required('Ce champ est obligatoire.'),
});

const isSubmitting = ref(false);

function submit() {
    isSubmitting.value = true;

    axios
        .post(
            route('front.projectManagement.apikom.postNewsTheme', {
                ...(newsTheme ? { newsTheme: newsTheme.id } : {}),
            }),
            {
                name: form.name,
                color: form.color,
                position: Number(form.position),
            },
        )
        .then(() => {
            toastSaved();

            router.visit(
                route('front.projectManagement.apikom.newsArticles', {
                    tab: 'newsThemes',
                }),
            );
        })
        .catch((error) => {
            toastError(error);
        })
        .finally(() => {
            isSubmitting.value = false;
        });
}
</script>

<template>
    <FormRoot
        :validation-schema="validationSchema"
        :submit="submit"
        v-slot="{ errors }"
    >
        <FormColumn>
            <FormContent>
                <FormGrid :columns="3">
                    <InputField
                        v-model="form.name"
                        label="Nom"
                        field-name="name"
                        field-id="name"
                        required
                    />

                    <ColorField
                        v-model="form.color"
                        label="Couleur"
                        legend="Couleur de la pastille affichée sur l'actualité côté client."
                        field-name="color"
                        field-id="color"
                        :preset-colors="presetColors"
                        required
                    />

                    <InputField
                        v-model="form.position"
                        label="Position"
                        legend="Détermine l'ordre d'affichage des thèmes dans les listes."
                        field-name="position"
                        field-id="position"
                        type="number"
                        step="1"
                        min="0"
                        required
                    />
                </FormGrid>
            </FormContent>
        </FormColumn>

        <FormFooter :is-submitting="isSubmitting" :errors="errors">
            <template #actions>
                <DeleteAction
                    v-if="newsTheme"
                    :url="
                        route(
                            'front.projectManagement.apikom.deleteNewsTheme',
                            {
                                newsTheme: newsTheme.id,
                            },
                        )
                    "
                    :callback="
                        () => {
                            router.visit(
                                route(
                                    'front.projectManagement.apikom.newsArticles',
                                    {
                                        tab: 'newsThemes',
                                    },
                                ),
                            );
                        }
                    "
                    content="Êtes-vous sûr de vouloir supprimer ce thème ? Il ne doit être rattaché à aucune actualité."
                >
                    <template #trigger>
                        <ActionButton type="secondary">
                            Supprimer
                        </ActionButton>
                    </template>
                </DeleteAction>
            </template>
        </FormFooter>
    </FormRoot>
</template>
